import { apiClient } from './authService';

/* ------------------------------------------------------------------ */
/*  Helpers — unwrap API response shapes from JsonResponseHelper        */
/*                                                                      */
/*  JsonResponseHelper::success  → { success, data, message }          */
/*  JsonResponseHelper::paginate → { success, data:[...], meta:{...} } */
/* ------------------------------------------------------------------ */
const unwrap = (response) => response.data?.data ?? response.data;

const unwrapPaginated = (response) => ({
  data:         response.data?.data                    ?? [],
  total:        response.data?.meta?.total             ?? 0,
  per_page:     response.data?.meta?.per_page          ?? 15,
  current_page: response.data?.meta?.current_page      ?? 1,
  last_page:    response.data?.meta?.last_page         ?? 1,
});

/* ------------------------------------------------------------------ */
/*  Dashboard                                                           */
/* ------------------------------------------------------------------ */
const getDashboardStats = async () => {
  const response = await apiClient.get('/customer/dashboard/stats');
  return unwrap(response);
};

/* ------------------------------------------------------------------ */
/*  Invoices                                                            */
/* ------------------------------------------------------------------ */
const getInvoices = async (params = {}, signal) => {
  const response = await apiClient.get('/customer/invoices', { params, signal });
  return unwrapPaginated(response);
};

const getInvoice = async (publicId) => {
  const response = await apiClient.get(`/customer/invoices/${publicId}`);
  return unwrap(response);
};

/**
 * Uses the existing SharePoint public PDF endpoint.
 * URL: sharepoint/{orgId}/sale/{publicId}/pdf/download/a4
 */
const downloadInvoicePDF = async (publicId, type = 'a4') => {
  const orgId = localStorage.getItem('organization_public_id');
  const response = await apiClient.get(
    `/sharepoint/${orgId}/sale/${publicId}/pdf/download/${type}`,
    { responseType: 'blob' }
  );
  return response.data;
};

/* ------------------------------------------------------------------ */
/*  Estimates                                                           */
/* ------------------------------------------------------------------ */
const getEstimates = async (params = {}, signal) => {
  const response = await apiClient.get('/customer/estimates', { params, signal });
  return unwrapPaginated(response);
};

const getEstimate = async (publicId) => {
  const response = await apiClient.get(`/customer/estimates/${publicId}`);
  return unwrap(response);
};

const acceptEstimate = async (publicId) => {
  const response = await apiClient.post(`/customer/estimates/${publicId}/accept`);
  return unwrap(response);
};

const declineEstimate = async (publicId, data = {}) => {
  const response = await apiClient.post(`/customer/estimates/${publicId}/decline`, data);
  return unwrap(response);
};

/**
 * Uses the existing SharePoint public PDF endpoint.
 * URL: sharepoint/{orgId}/estimate/{publicId}/pdf/download/a4
 */
const downloadEstimatePDF = async (publicId, type = 'a4') => {
  const orgId = localStorage.getItem('organization_public_id');
  const response = await apiClient.get(
    `/sharepoint/${orgId}/estimate/${publicId}/pdf/download/${type}`,
    { responseType: 'blob' }
  );
  return response.data;
};

/* ------------------------------------------------------------------ */
/*  Workspace events — list + per-document feed                         */
/* ------------------------------------------------------------------ */
const getWorkspaceEventsUnreadCount = async () => {
  const response = await apiClient.get('/customer/workspace_events/unread_count');
  const payload = response?.data?.data ?? response?.data;
  const n = Number(payload?.unread_count);
  return Number.isFinite(n) ? n : 0;
};

const listWorkspaceEvents = async () => {
  const response = await apiClient.get('/customer/workspace_events');
  return unwrap(response) ?? [];
};


const getInvoiceWorkspaceFeed = async (publicId) => {
  return apiClient.get(`/customer/invoices/${publicId}/workspace_feed`);
};

const getEstimateWorkspaceFeed = async (publicId) => {
  return apiClient.get(`/customer/estimates/${publicId}/workspace_feed`);
};

const getWorkspace = async (publicId) => {
  const response = await apiClient.get(`/customer/workspaces/${publicId}`);
  return unwrap(response);
};

const getWorkspaceFeed = async (publicId) => {
  return apiClient.get(`/customer/workspaces/${publicId}/workspace_feed`);
};

const postInvoiceWorkspaceComment = async (publicId, data = {}) => {
  const response = await apiClient.post(`/customer/invoices/${publicId}/workspace_comments`, data);
  return unwrap(response);
};

const postEstimateWorkspaceComment = async (publicId, data = {}) => {
  const response = await apiClient.post(`/customer/estimates/${publicId}/workspace_comments`, data);
  return unwrap(response);
};

const postInvoiceWorkspaceMedia = async (publicId, file, displayName) => {
  const formData = new FormData();
  formData.append('file', file);
  if (displayName) {
    formData.append('display_name', displayName);
  }
  const response = await apiClient.post(`/customer/invoices/${publicId}/workspace_media`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(response);
};

const postEstimateWorkspaceMedia = async (publicId, file, displayName) => {
  const formData = new FormData();
  formData.append('file', file);
  if (displayName) {
    formData.append('display_name', displayName);
  }
  const response = await apiClient.post(`/customer/estimates/${publicId}/workspace_media`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(response);
};

const postInvoiceEvent = async (publicId, formData) => {
  return apiClient.post(`/customer/invoices/${publicId}/workspace_events`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

const postInvoiceEventReply = async (publicId, formData) => {
  const response = await apiClient.post(
    `/customer/invoices/${publicId}/workspace_event_replies`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return unwrap(response);
};

const postEstimateEvent = async (publicId, formData) => {
  return apiClient.post(`/customer/estimates/${publicId}/workspace_events`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

const postEstimateEventReply = async (publicId, formData) => {
  const response = await apiClient.post(
    `/customer/estimates/${publicId}/workspace_event_replies`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return unwrap(response);
};

const postWorkspaceEvent = async (publicId, formData) => {
  return apiClient.post(`/customer/workspaces/${publicId}/workspace_events`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

const postWorkspaceEventReply = async (publicId, formData) => {
  const response = await apiClient.post(
    `/customer/workspaces/${publicId}/workspace_event_replies`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return unwrap(response);
};

const markEventRead = async (documentType, entityPublicId, workspaceEventPublicId) => {
  const base =
    documentType === 'invoice'   ? `/customer/invoices/${entityPublicId}`   :
    documentType === 'estimate'  ? `/customer/estimates/${entityPublicId}`  :
                                   `/customer/workspaces/${entityPublicId}`;
  return apiClient.patch(`${base}/workspace_events/${workspaceEventPublicId}/mark_read`);
};

const customerService = {
  getDashboardStats,
  listWorkspaceEvents,
  getWorkspaceEventsUnreadCount,
  getInvoices,
  getInvoice,
  downloadInvoicePDF,
  getEstimates,
  getEstimate,
  acceptEstimate,
  declineEstimate,
  downloadEstimatePDF,
  getInvoiceWorkspaceFeed,
  getEstimateWorkspaceFeed,
  getWorkspace,
  getWorkspaceFeed,
  postInvoiceWorkspaceComment,
  postEstimateWorkspaceComment,
  postInvoiceWorkspaceMedia,
  postEstimateWorkspaceMedia,
  postInvoiceEvent,
  postInvoiceEventReply,
  postEstimateEvent,
  postEstimateEventReply,
  postWorkspaceEvent,
  postWorkspaceEventReply,
  markEventRead,
};

export default customerService;
